// Minimal RFC-4180-ish CSV parsing for bulk-upload previews.
//
// Handles quoted fields, commas inside quotes, and escaped double-quotes ("").
// Not a full streaming parser — adequate for the small operator-supplied files
// the bulk-upload modal previews client-side before sending the raw file to BE.

/**
 * Splits a single CSV line into fields, respecting double-quoted segments
 * (so a quoted value containing a comma stays one field). Escaped quotes ("")
 * inside a quoted field collapse to a single quote.
 *
 * @param {string} line
 * @returns {string[]}
 */
export function parseCsvLine(line) {
  const fields = [];
  let field = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (inQuotes) {
      if (ch === '"') {
        if (line[i + 1] === '"') { field += '"'; i++; } // escaped quote
        else inQuotes = false;
      } else {
        field += ch;
      }
    } else if (ch === '"') {
      inQuotes = true;
    } else if (ch === ',') {
      fields.push(field);
      field = '';
    } else {
      field += ch;
    }
  }
  fields.push(field);
  return fields.map((f) => f.trim());
}

/**
 * Parses CSV text into an array of row objects keyed by the header row.
 * Blank lines are skipped. Missing trailing cells become ''.
 *
 * @param {string} text
 * @returns {{ headers: string[], rows: Record<string, string>[] }}
 */
export function parseCsv(text) {
  const lines = text.split(/\r?\n/).filter((l) => l.trim());
  if (lines.length === 0) return { headers: [], rows: [] };
  const headers = parseCsvLine(lines[0]);
  const rows = lines.slice(1).map((line) => {
    const vals = parseCsvLine(line);
    const obj = {};
    headers.forEach((h, i) => { obj[h] = vals[i] ?? ''; });
    return obj;
  });
  return { headers, rows };
}
